import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  Typography,
  Button,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Add,
} from '@mui/icons-material'
import TicketList from '../components/TicketList'
import ConversationView from '../components/ConversationView'
import CreateTicketDialog from '../components/CreateTicketDialog'
import { useAuth } from '../hooks/useAuth'

const DEFAULT_DRAWER_WIDTH = 360
const MIN_DRAWER_WIDTH = 240
const MAX_DRAWER_WIDTH = 600
const COLLAPSED_DRAWER_WIDTH = 96

const TicketViewer = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { ticketId } = useParams()
  const navigate = useNavigate()

  // Auth integration
  const { user } = useAuth()

  // Selection state
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [createdTicketLoading, setCreatedTicketLoading] = useState(false)

  // Stable refs to list functions — registered by TicketList on mount.
  const patchTicketRowRef = useRef(null)
  const prependTicketRef = useRef(null)

  // UI state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [createTicketOpen, setCreateTicketOpen] = useState(false)

  // Helper functions for localStorage
  const getStoredDrawerState = () => {
    try {
      const stored = localStorage.getItem('agentflow-drawer-state')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.warn('Failed to parse drawer state from localStorage:', error)
    }
    return {
      collapsed: true,
      width: DEFAULT_DRAWER_WIDTH
    }
  }

  const saveDrawerState = (state) => {
    try {
      localStorage.setItem('agentflow-drawer-state', JSON.stringify(state))
    } catch (error) {
      console.warn('Failed to save drawer state to localStorage:', error)
    }
  }

  // Desktop drawer state - initialize from localStorage
  const [drawerState, setDrawerState] = useState(() => getStoredDrawerState())
  const [isResizing, setIsResizing] = useState(false)

  // Extract individual state values for easier access
  const drawerCollapsed = drawerState.collapsed
  const drawerWidth = drawerState.width

  // URL sync - update selectedTicketId when URL changes
  useEffect(() => {
    const targetId = ticketId ? parseInt(ticketId) : null
    if (targetId !== selectedTicketId) {
      setSelectedTicketId(targetId)
    }
  }, [ticketId, selectedTicketId])

  // Handle ticket selection
  const handleTicketSelect = useCallback((ticket) => {
    const id = ticket.id
    setSelectedTicketId(id)
    navigate(`/tickets/${id}`)

    // Close mobile drawer
    if (isMobile) {
      setMobileDrawerOpen(false)
    }

    // Auto-collapse desktop drawer when selecting a ticket
    if (!isMobile && !drawerCollapsed) {
      const newState = { ...drawerState, collapsed: true }
      setDrawerState(newState)
      saveDrawerState(newState)
    }
  }, [navigate, isMobile, drawerCollapsed, drawerState])

  // Handle ticket creation success
  const handleTicketCreated = useCallback((newTicket) => {
    prependTicketRef.current?.(newTicket)
    setCreatedTicketLoading(true)
    setSelectedTicketId(newTicket.id)
    navigate(`/tickets/${newTicket.id}`)

    // Auto-collapse desktop drawer when creating a ticket
    if (!isMobile && !drawerCollapsed) {
      const newState = { ...drawerState, collapsed: true }
      setDrawerState(newState)
      saveDrawerState(newState)
    }

    // Clear loading state after a brief delay
    setTimeout(() => setCreatedTicketLoading(false), 500)
  }, [navigate, isMobile, drawerCollapsed, drawerState])

  // Handle ticket updated (for delete, archive, etc.) or a status/role patch object.
  const handleTicketUpdated = useCallback((action) => {
    if (action === 'deleted' || action === 'archived') {
      setSelectedTicketId(null)
      navigate('/tickets')
    } else if (action && typeof action === 'object') {
      patchTicketRowRef.current?.(selectedTicketId, action)
    }
  }, [navigate, selectedTicketId])

  // Mobile drawer toggle
  const toggleDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen)
  }

  // Desktop drawer controls
  const toggleDrawerCollapsed = () => {
    const newState = { ...drawerState, collapsed: !drawerCollapsed }
    setDrawerState(newState)
    saveDrawerState(newState)
  }

  // Resize functionality
  const handleResizeStart = useCallback((e) => {
    e.preventDefault()
    setIsResizing(true)

    const startX = e.clientX
    const startWidth = drawerWidth

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX
      const newWidth = Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, startWidth + deltaX))
      const newState = { ...drawerState, width: newWidth }
      setDrawerState(newState)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      saveDrawerState(drawerState)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [drawerWidth, drawerState])

  // Calculate effective drawer width
  const effectiveDrawerWidth = drawerCollapsed ? COLLAPSED_DRAWER_WIDTH : drawerWidth

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Desktop Drawer Controls */}
      {!isMobile && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48
        }}>
          {!drawerCollapsed && (
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Tickets
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: drawerCollapsed ? 'center' : 'flex-end', width: '100%' }}>
            <IconButton
              size="small"
              onClick={toggleDrawerCollapsed}
              title={drawerCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {drawerCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Ticket List */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <TicketList
          selectedTicketId={selectedTicketId}
          onTicketSelect={handleTicketSelect}
          createdTicketLoading={createdTicketLoading}
          onCreateTicket={() => setCreateTicketOpen(true)}
          collapsed={!isMobile && drawerCollapsed}
          user={user}
          onRegisterPatch={(fn) => { patchTicketRowRef.current = fn }}
          onRegisterPrepend={(fn) => { prependTicketRef.current = fn }}
        />
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box sx={{ position: 'relative', display: 'flex' }}>
          <Drawer
            variant="permanent"
            sx={{
              width: effectiveDrawerWidth,
              flexShrink: 0,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
              '& .MuiDrawer-paper': {
                width: effectiveDrawerWidth,
                boxSizing: 'border-box',
                position: 'relative',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.standard,
                }),
                overflow: 'hidden',
              },
            }}
          >
            {drawerContent}
          </Drawer>

          {/* Resize Handle */}
          {!drawerCollapsed && (
            <Box
              sx={{
                width: 4,
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                transition: 'background-color 0.2s',
                position: 'relative',
                zIndex: 1,
              }}
              onMouseDown={handleResizeStart}
            />
          )}
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DEFAULT_DRAWER_WIDTH,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Mobile Header */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <IconButton onClick={toggleDrawer} edge="start">
              <ChevronRight />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Tickets
            </Typography>
          </Box>
        )}

        {/* Conversation View */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {selectedTicketId ? (
            <ConversationView
              ticketId={selectedTicketId}
              onTicketUpdated={handleTicketUpdated}
            />
          ) : (
            /* Select Ticket State */
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 4,
              gap: 2
            }}>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  textAlign: 'center'
                }}
              >
                Hi, {user?.username || 'there'}! Select a ticket or create one below
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateTicketOpen(true)}
              >
                Create Ticket
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Create Ticket Dialog */}
      <CreateTicketDialog
        open={createTicketOpen}
        onClose={() => setCreateTicketOpen(false)}
        onTicketCreated={handleTicketCreated}
      />
    </Box>
  )
}

export default TicketViewer
